import { createMuiTheme } from "@material-ui/core/styles";

// Centralized brand colors
export const brandColors = {
  primary: {
    main: "#E50052",       // Yellow/orange
    light: "#ffd588",
  },
  secondary: {
    main: "#ff441a",       // Red/orange
    light: "#ffa18c",
    bg: "#ffdad1",
  },
  tertiary: {
    main: "#012169",
  },
  pdf: {
    yellow: "#ffac3a",
    yellowLight: "#ffeed7",
    green: "#154734",
    greenLight: "#d0dad6",
  },
  success: {
    bright: "#8fcc60",       // Green
    bg: "#F0FDF4",
    bgAlt: "#D1E5D9",
    status: "#01823b",
  },
  error: {
    main: "#ff441a",       // Red
    light: "#ffa18c",
    bright: "#ff4319",
  },
};

const themeOptions = {
  props: {
    MuiButtonBase: {
      disableRipple: true,
    },
  },
  palette: {
    common: { black: "#1f2432", white: "#ffffff" },
    primary: {
      light: brandColors.primary.light,
      main: brandColors.primary.main,
      contrastText: "#ffffff",
    },
    secondary: {
      main: brandColors.secondary.main,
      light: brandColors.secondary.light,
      contrastText: "#ffffff",
    },
    tertiary: {
      main: brandColors.tertiary.main,
      contrastText: "#ffffff",
    },
    action: {
      active: brandColors.primary.main,
      disabled: "#c2d1d9",
    },
    error: { main: brandColors.error.main, light: brandColors.error.light },
    grey: {
      50: "#f2f5f7",
      300: "#c2d1d9",
      600: "#a1aeb7",
      900: "#424b5a",
      400: "#505D68",
    },
    text: { primary: "#424b5a", secondary: "#a1aeb7" },
    background: { paper: "#ffffff", default: "#f2f5f7" },
  },
  typography: {
    fontFamily: "Ubuntu",
    h1: { fontSize: "66px" },
    h2: { fontSize: "45px", lineHeight: 1.31 },
    h3: { fontSize: "38px", fontWeight: "bold", lineHeight: 1.26 },
    h4: { fontSize: "28px", fontWeight: "bold", lineHeight: 1.36 },
    h5: { fontSize: "21px", fontWeight: "bold", lineHeight: 1.48 },
    h6: { fontSize: "18px", fontWeight: 500, lineHeight: 1.33 },
    body1: { fontSize: "16px", lineHeight: 1.5 },
    body2: { fontSize: "13px", lineHeight: 1.5 },
    button: { fontSize: "10px", fontWeight: 300, letterSpacing: "1px" },
  },
  overrides: {
    MuiInput: {
      root: {
        color: "#303030",
        background: "#ffffff",
        borderRadius: 1,
        padding: "8px 10px",
      },
      input: {
        "&:-webkit-autofill": {
          WebkitBoxShadow: "0 0 0px 1000px white inset",
          transition: "background-color 5000s ease-in-out 0s",
        },
        "&::placeholder": {
          textOverflow: "ellipsis !important",
          color: "#c2d1d9",
          opacity: 1,
        },
      },
    },
    MuiInputBase: {
      root: {
        "&.Mui-disabled": {
          backgroundColor: "#f2f5f7",
        },
      },
      input: {
        "&.Mui-disabled": {
          backgroundColor: "#f2f5f7",
        },
      },
    },
    MuiOutlinedInput: {
      input: {
        backgroundColor: "#ffffff",
        padding: "11px",
        "&:-webkit-autofill": {
          WebkitBoxShadow: "0 0 0px 1000px white inset",
          transition: "background-color 5000s ease-in-out 0s",
        },
        "&::placeholder": {
          textOverflow: "ellipsis !important",
          color: "#c2d1d9",
          opacity: 1,
        },
      },
      root: {
        position: "relative",
        "& $notchedOutline": {
          borderColor: "#c2d1d9",
        },
        "&:hover:not($disabled):not($focused):not($error) $notchedOutline": {
          borderColor: "#c2d1d9",
        },
        "&$focused $notchedOutline": {
          borderColor: "#c2d1d9",
          borderWidth: 1,
        },
      },
    },
    MuiButton: {
      contained: {
        boxShadow: "none",
        fontSize: "14px",
        lineHeight: "20px",
        fontWeight: 400,
        letterSpacing: "-0.15px",
        backgroundColor: brandColors.tertiary.main,
        color: "#ffffff",
        "&:hover": {
          backgroundColor: brandColors.tertiary.main,
          filter: "brightness(1.1)",
        },
        "&$disabled": {
          backgroundColor: "#c2d1d9",
          color: "#424b5a",
        },
      },
      outlined: {
        border: "1px solid",
        fontSize: "14px",
        lineHeight: "20px",
        fontWeight: 400,
        letterSpacing: "-0.15px",
        borderColor: brandColors.tertiary.main,
        color: brandColors.tertiary.main,
        "&:hover": {
          borderColor: brandColors.tertiary.main,
          backgroundColor: brandColors.tertiary.main,
          color: "#ffffff",
        },
        "&$disabled": {
          borderColor: "#c2d1d9",
          color: "#424b5a",
        },
      },
    },
    MuiIconButton: {
      root: {
        "&:hover": {
          backgroundColor: "transparent",
        },
      },
    },
    MuiPaper: {
      elevation1: {
        boxShadow: "-10px 10px 20px 0 rgba(30, 30, 30, 0.05)",
      },
    },
    MuiRadio: {
      root: {
        color: brandColors.primary.main,
      },
    },
    MuiExpansionPanelSummary: {
      root: {
        margin: "0px",
        minHeight: "0px",
        "&$expanded": {
          margin: "0px",
          minHeight: "0px",
        },
      },
      content: {
        margin: "0px",
        minHeight: "0px",
        "&$expanded": {
          margin: "0px",
          minHeight: "0px",
        },
      },
    },
    MuiFab: {
      root: {
        boxShadow: "none",
      },
      sizeSmall: {
        width: "24px",
        height: "24px",
        minHeight: "auto",
        "&:active": {
          boxShadow: "none",
        },
      },
    },
    MuiTableSortLabel: {
      root: { display: "flex", justifyContent: "space-between" },
    },
    MuiList: {
      root: { outline: "none" },
    },
  },
};

const theme = createMuiTheme(
    themeOptions as Parameters<typeof createMuiTheme>[0]
);

export default theme;
